import {
  Button,
  Checkbox,
  CheckboxOptionType,
  Col,
  Layout,
  Radio,
  RadioChangeEvent,
  Row,
} from 'antd';
import { stringify } from 'csv-stringify/browser/esm/sync';
import FileSaver from 'file-saver';
import { useState } from 'react';
import type { DerivedTeamRoster, StoredShiftEvent } from '../../hooks/types';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import { usePermission } from '../../hooks/usePermission';
import { useNotify } from '../../hooks/useNotify';
import { onApolloError } from '../../lib/graphql/helpers';
import {
  rostersToCsvData,
  shiftEventCsvColumns,
  shiftEventToCsvRows,
} from '../../lib/utils/csv-report';
import DatePicker, { onRangeChange } from '../DatePicker';
import HeaderSection from '../HeaderSection';
import ExportDataSearch from './ExportDataSearch';
import {
  useCostObjectSearchParams,
  useShiftSearchParams,
  useTeamRoleGroupSearchParams,
  useTeamRoleSearchParams,
  useTeamSearchParams,
  useTeamTypeSearchParams,
  useUserSearchParams,
  useUserTypeSearchParams,
} from './lib/util';
import ExportShiftEventTable from './ExportShiftEventTable';
import ExportShiftTable from './ExportShiftTable';

const { RangePicker } = DatePicker;

const searchModes = {
  shift: 'shift',
  shiftEvent: 'shiftEvent',
};

type SearchModeValue = typeof searchModes;

type SearchMode = keyof SearchModeValue;

const searchModeOptions: readonly CheckboxOptionType[] = [
  {
    label: 'Search for shifts',
    value: 'shift',
  },
  {
    label: 'Search for shift events',
    value: 'shiftEvent',
  },
];

/**
 * Renders a page that provides NSW RFS IMT searching with multi params.
 */
const ExportData = () => {
  const { notify } = useNotify();
  const { loggedInUser } = useLoggedInUser();
  const permission = usePermission(undefined, loggedInUser);

  const [searchMode, setSearchMode] = useState<SearchMode>('shift');
  const [message, setMessage] = useState('');

  // Queried team roster for export
  const [queriedTeamRoster, setQueriedTeamRoster] = useState<
    readonly DerivedTeamRoster[]
  >([]);

  // Shift event for export
  const [shiftEvent, setShiftEvent] = useState<readonly StoredShiftEvent[]>([]);

  ////////////////////////
  // Team search params //
  ////////////////////////
  const {
    loadingTeams,
    teamOptions,
    selectedTeamIds,
    setTeamSearchText,
    setSelectedTeamIds,
  } = useTeamSearchParams(onApolloError(notify.error));

  /////////////////////////////
  // Team type search params //
  /////////////////////////////
  const { teamTypeOptions, selectedTeamTypes, setSelectedTeamTypes } =
    useTeamTypeSearchParams();

  ///////////////////////////////
  // Cost object search params //
  ///////////////////////////////
  const {
    loadingCostObjects,
    costObjectOptions,
    selectedCostObjectIds,
    setCostObjectSearchText,
    setSelectedCostObjectIds,
  } = useCostObjectSearchParams(onApolloError(notify.error));

  /////////////////////////////
  // Team role search params //
  /////////////////////////////
  const {
    loadingTeamRoles,
    teamRoleOptions,
    selectedTeamRoleIds,
    setTeamRoleSearchText,
    setSelectedTeamRoleIds,
  } = useTeamRoleSearchParams(onApolloError(notify.error));

  ///////////////////////////////////
  // Team role group search params //
  ///////////////////////////////////
  const {
    roleGroupOptions,
    selectedTeamRoleGroups,
    setSelectedTeamRoleGroups,
  } = useTeamRoleGroupSearchParams();

  /**
   * Shift filter used for display and export. Determined by only vacant check box and personnel filters.
   */
  const [shiftFilter, setShiftFilter] = useState<
    'only vacant shifts' | 'only non-vacant shifts' | 'all shifts'
  >('all shifts');

  /**
   * Only vacant shifts that is set by only vacant check box.
   */
  const [onlyVacant, setOnlyVacant] = useState(false);

  ////////////////////////
  // User search params //
  ////////////////////////
  const {
    loadingUsers,
    userOptions,
    selectedUserIds,
    setUserSearchText,
    setSelectedUserIds,
  } = useUserSearchParams(onApolloError(notify.error));

  /////////////////////////////
  // User type search params //
  /////////////////////////////
  const {
    loadingUserTypes,
    userTypeOptions,
    selectedUserTypes,
    setSelectedUserTypes,
  } = useUserTypeSearchParams(onApolloError(notify.error));

  //////////////////////////////////////////////////////////
  // Shift time or shift event created time search params //
  //////////////////////////////////////////////////////////
  const { fromDate, toDate, setFromDate, setToDate } = useShiftSearchParams();

  ////////////////////////////////
  // Define component functions //
  ////////////////////////////////
  const exportData = () => {
    const tableData: readonly (readonly string[])[] =
      searchMode === searchModes.shift
        ? rostersToCsvData(
            queriedTeamRoster,
            permission.isPowerUser,
            shiftFilter
          )
        : [shiftEventCsvColumns, ...shiftEvent.map(shiftEventToCsvRows)];

    const csvText = stringify([...tableData]);

    const fileName =
      searchMode === searchModes.shift
        ? 'Exported Shift Data.csv'
        : 'Exported Shift Event Data.csv';

    const data = new Blob([csvText], { type: 'text/csv' });

    FileSaver.saveAs(data, fileName);
  };

  /**
   * Handles the change of the only vacant check box.
   * @param newOnlyVacant - the new value of the only vacant check box
   */
  const handleOnlyVacantChange = (newOnlyVacant: boolean) => {
    const thereIsAPersonnelFilter =
      selectedUserIds.length > 0 || selectedUserTypes.length > 0;

    setOnlyVacant(newOnlyVacant);

    if (newOnlyVacant) {
      setShiftFilter('only vacant shifts');
    }
    if (!newOnlyVacant && thereIsAPersonnelFilter) {
      setShiftFilter('only non-vacant shifts');
    }
    if (!newOnlyVacant && !thereIsAPersonnelFilter) {
      setShiftFilter('all shifts');
    }
  };

  /**
   * Sets the shift filter based on the changes in the personnel filters.
   * @param changedFilterLength - the length of the changed filter
   * @param dependentFilterLength - the length of the dependent filter
   */
  const handleShiftFilterChange = (
    changedFilterLength: number,
    dependentFilterLength: number
  ) => {
    if (changedFilterLength === 0 && dependentFilterLength === 0) {
      setShiftFilter('all shifts');
    }
    if (changedFilterLength > 0) {
      setShiftFilter('only non-vacant shifts');
    }
  };

  const onSearchModeChange = (e: RadioChangeEvent) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const nextSearchMode = e.target.value as SearchMode;

    setSearchMode(nextSearchMode);
    handleOnlyVacantChange(false);
  };

  const commonProps = {
    fromDate,
    toDate,
    selectedCostObjectIds,
    selectedTeamIds,
    selectedTeamRoleGroups,
    selectedTeamRoleIds,
    selectedTeamTypes,
    selectedUserIds,
    selectedUserTypes,
    setMessage,
  };

  return (
    <Layout>
      <HeaderSection title={'Export Data'} />
      <Row>
        <Col>
          <Radio.Group
            id="export-data-search-mode-radio-group"
            options={[...searchModeOptions]}
            onChange={onSearchModeChange}
            value={searchMode}
            optionType="button"
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={2}>
          <label htmlFor="export-data-team-search">Team:</label>
        </Col>
        <Col span={10}>
          <ExportDataSearch
            id="export-data-team-search"
            selectedValues={selectedTeamIds}
            options={teamOptions}
            setSearchText={setTeamSearchText}
            setSelectedValues={setSelectedTeamIds}
            loading={loadingTeams}
          />
        </Col>
        <Col offset={1} span={3}>
          <label htmlFor="export-data-team-type-select">Team Type:</label>
        </Col>
        <Col span={6}>
          <ExportDataSearch
            id="export-data-team-type-select"
            selectedValues={selectedTeamTypes}
            options={teamTypeOptions}
            setSelectedValues={setSelectedTeamTypes}
            showArrow
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={2}>
          <label htmlFor="export-data-cost-object-search">Cost Object:</label>
        </Col>
        <Col span={10}>
          <ExportDataSearch
            id="export-data-cost-object-search"
            selectedValues={selectedCostObjectIds}
            options={costObjectOptions}
            setSearchText={setCostObjectSearchText}
            setSelectedValues={setSelectedCostObjectIds}
            loading={loadingCostObjects}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={2}>
          <label htmlFor="export-data-team-role-search">Team Role:</label>
        </Col>
        <Col span={10}>
          <ExportDataSearch
            id="export-data-team-role-search"
            selectedValues={selectedTeamRoleIds}
            options={teamRoleOptions}
            setSearchText={setTeamRoleSearchText}
            setSelectedValues={setSelectedTeamRoleIds}
            loading={loadingTeamRoles}
          />
        </Col>
        <Col offset={1} span={3}>
          <label htmlFor="export-data-team-role-group-select">
            Role Group:
          </label>
        </Col>
        <Col span={6}>
          <ExportDataSearch
            id="export-data-team-role-group-select"
            selectedValues={selectedTeamRoleGroups}
            options={roleGroupOptions}
            setSelectedValues={setSelectedTeamRoleGroups}
            showArrow
          />
        </Col>
      </Row>
      {searchMode === searchModes.shift && (
        <Row gutter={16} style={{ marginTop: '10px', marginBottom: '10px' }}>
          <Col span={2}>
            <label htmlFor="export-data-vacant-checkbox">Only Vacant:</label>
          </Col>
          <Col span={10}>
            <Checkbox
              id="export-data-vacant-checkbox"
              checked={onlyVacant}
              style={{}}
              onChange={(e) => {
                handleOnlyVacantChange(e.target.checked);
              }}
            />
          </Col>
        </Row>
      )}
      <Row
        gutter={16}
        style={{
          transition: 'all 0.5s ease-in-out',
          height: 'auto',
          marginTop: onlyVacant ? '0' : '10px',
          opacity: onlyVacant ? '0' : '1',
          visibility: onlyVacant ? 'hidden' : 'visible',
          maxHeight: onlyVacant ? '0' : '100%',
        }}
      >
        <Col span={2}>
          <label htmlFor="export-data-personnel-search">Personnel:</label>
        </Col>
        <Col span={10}>
          <ExportDataSearch
            id="export-data-personnel-search"
            selectedValues={selectedUserIds}
            options={userOptions}
            setSearchText={setUserSearchText}
            setSelectedValues={(newSelectedUserIds) => {
              setSelectedUserIds(newSelectedUserIds);

              handleShiftFilterChange(
                newSelectedUserIds.length,
                selectedUserTypes.length
              );
            }}
            loading={loadingUsers}
          />
        </Col>
        <Col offset={1} span={3}>
          <label htmlFor="export-data-user-type-select">Personnel Type:</label>
        </Col>
        <Col span={6}>
          <ExportDataSearch
            id="export-data-user-type-select"
            selectedValues={selectedUserTypes}
            options={userTypeOptions}
            setSelectedValues={(newSelectedUserTypes) => {
              setSelectedUserTypes(newSelectedUserTypes);

              handleShiftFilterChange(
                newSelectedUserTypes.length,
                selectedUserIds.length
              );
              if (
                selectedUserIds.length === 0 &&
                newSelectedUserTypes.length === 0
              ) {
                setShiftFilter('all shifts');
              }
              if (newSelectedUserTypes.length > 0) {
                setShiftFilter('only non-vacant shifts');
              }
            }}
            showArrow
            loading={loadingUserTypes}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={2}>
          <label htmlFor="export-data-shift-search">
            {searchMode === searchModes.shift ? 'Shift:' : 'Shift event:'}
          </label>
        </Col>
        <Col span={8}>
          <RangePicker
            id="export-data-shift-search"
            allowClear={false}
            direction="ltr"
            value={[fromDate, toDate]}
            onChange={onRangeChange(setFromDate, setToDate)}
          />
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: '10px' }}>
        <Col span={20}>
          <p style={{ color: 'red' }}>{message}</p>
        </Col>
        <Col span={4}>
          <Button
            className="export-roster-btn"
            style={{
              float: 'right',
              minWidth: '120px',
              marginRight: '10px',
            }}
            type="primary"
            onClick={exportData}
          >
            Export to CSV
          </Button>
        </Col>
      </Row>
      {searchMode === searchModes.shift ? (
        <ExportShiftTable
          {...{
            ...commonProps,
            shiftFilter,
            queriedTeamRoster,
            setQueriedTeamRoster,
          }}
        />
      ) : (
        <ExportShiftEventTable
          {...{
            ...commonProps,
            setShiftEvent,
            shiftEvent,
          }}
        />
      )}
    </Layout>
  );
};

export default ExportData;
